import { useState, useEffect, useTransition, useRef, useMemo } from 'react';
import { useSearchParams } from 'next/navigation';
import { useRouter } from '@/navigation';
import { useLocale } from 'next-intl';
import { ScrollArea } from '@radix-ui/react-scroll-area';
import { scrollIntoView, scrollTo } from 'seamless-scroll-polyfill';

import { Course, Page } from '@/payload/payload-types';

import { CourseListCard } from '../../_components/Courses/ListCard';
import { Spacer } from '../Spacer';
import { useFullSlug } from '../../_contexts/FullSlugContext';
import RichTextLexical from '../../_components/RichTextLexical';
import FilterButton from './FilterButton';
import { Button } from '../../_components/Button';
import { useHeaderHeight } from '../../_providers/HeaderHeight';
import { logToPayload } from '../../_utilities/log-to-payload';
import { ListCourseType } from '../../_utilities/typescript';

type FilteredProductsType = Extract<Page['layoutBlocks'][0], { blockType: 'filteredProducts' }>;
type FilteredProductsProps = Omit<FilteredProductsType, 'blockType'>;
type FilterType = FilteredProductsType['filters'][0];

export const FilteredProducts: React.FC<FilteredProductsProps> = ({
	filters,
	allProductsFilter,
}) => {
	const router = useRouter();
	const searchParams = useSearchParams();
	const locale = useLocale();
	const fullSlug = useFullSlug();

	const categoryParam = searchParams.get('category');

	// Memoized function to get all unique courses from filters
	const getAllItemsFromFilters = useMemo(() => {
		const allItems = filters.flatMap(filter => filter.itemsInProductFilter);
		const uniqueItemsMap = new Map<string, Course>();

		allItems.forEach(item => {
			if (typeof item === 'string') {
				logToPayload('Bad depth or course is not available at getAllItemsFromFilters: ', {
					course: item,
				});
			}

			if (typeof item !== 'string' && item) {
				uniqueItemsMap.set(item.id, item);
			}
		});

		return Array.from(uniqueItemsMap.values());
	}, [filters]);

	// Memoized function to handle default filter logic
	const mappedFilters = useMemo(() => {
		const allProductsBtnTitle =
			allProductsFilter?.showAllProductsFilter && allProductsFilter.btnTitle;

		if (allProductsBtnTitle) {
			// Add "All products" filter at the beginning of the filters array
			const allItemsFilter = {
				btnTitle: allProductsBtnTitle,
				descriptionTitle: null,
				description: null,
				itemsInProductFilter: getAllItemsFromFilters,
				urlParam: null,
			};
			return [allItemsFilter, ...filters];
		} else {
			// Remove `urlParam` for the first filter
			return filters.map((filter, index) =>
				index === 0 ? { ...filter, urlParam: null } : filter,
			);
		}
	}, [filters, allProductsFilter, getAllItemsFromFilters]);

	const headerHeight = useHeaderHeight();
	const urlParamFilter = mappedFilters.find(({ urlParam }) => urlParam === categoryParam);
	const currentFilter: FilterType =
		categoryParam && urlParamFilter ? urlParamFilter : mappedFilters[0];

	const [isPending, startTransition] = useTransition();
	const [delayedPending, setDelayedPending] = useState<boolean>(false);
	const [activeFilter, setActiveFilter] = useState<FilterType>(currentFilter);
	const [isInitialLoad, setIsInitialLoad] = useState<boolean>(true);

	// Refs for filter buttons to scroll them into view
	const filterRowRef = useRef<HTMLDivElement | null>(null);
	const filterButtonRefs = useRef<(HTMLButtonElement | null)[]>([]);

	// Handle showing spinner on button with small delay
	useEffect(() => {
		let timeoutId: NodeJS.Timeout | undefined;

		if (isPending) {
			timeoutId = setTimeout(() => {
				setDelayedPending(true);
			}, 100);
		} else {
			setDelayedPending(false);
			clearTimeout(timeoutId);
		}
		return () => clearTimeout(timeoutId);
	}, [isPending]);

	// in case active filter changes, scroll the active filter to view and scroll the user to top
	useEffect(() => {
		const activeFilterIndex = mappedFilters.findIndex(filter => filter === activeFilter);
		if (!isInitialLoad) {
			if (filterButtonRefs.current[activeFilterIndex]) {
				scrollIntoView(filterButtonRefs.current[activeFilterIndex]!, {
					behavior: 'smooth',
					block: 'nearest',
					inline: 'center',
				});
			}
			if (filterRowRef.current) {
				const elementPosition =
					filterRowRef.current.getBoundingClientRect().top + window.scrollY;
				const offsetPosition = elementPosition - headerHeight - 24;

				//https://stackoverflow.com/questions/66586455/why-doesnt-scrollintoview-work-on-two-simultaneous-elements
				setTimeout(() => {
					scrollTo(window, {
						top: offsetPosition,
						behavior: 'smooth',
					});
				}, 500);
			}
		} else {
			// do not scroll user to top if active filter changes less than 1000ms after page load
			scrollTo(window, { top: 0, behavior: 'smooth' });
			setTimeout(() => {
				setIsInitialLoad(false);
			}, 1000);
		}
	}, [activeFilter, headerHeight]);

	// in case user clicks back/forward btn, make correct filter active
	// Handle invalid URL category and redirect to the default filter
	useEffect(() => {
		if (activeFilter && activeFilter.urlParam !== categoryParam) {
			const currentFilter =
				categoryParam && urlParamFilter ? urlParamFilter : mappedFilters[0];
			handleFilterChange(currentFilter);
		}

		if (!urlParamFilter && categoryParam) {
			router.replace(
				{
					// @ts-expect-error next-intl bad types
					pathname: fullSlug,
					query: mappedFilters[0]?.urlParam
						? { category: mappedFilters[0].urlParam }
						: undefined,
				},
				{
					scroll: false,
					locale,
				},
			);
		}
	}, [urlParamFilter, categoryParam, mappedFilters, router, fullSlug, locale]);

	const handleFilterChange = (filter: FilteredProductsType['filters'][0]) => {
		if (filter.btnTitle !== activeFilter?.btnTitle) {
			setActiveFilter(filter);
			startTransition(() => {
				router.push(
					{
						// @ts-expect-error fullslug mapping
						pathname: fullSlug,
						query: filter.urlParam ? { category: filter.urlParam } : undefined,
					},
					{ scroll: false, locale },
				);
			});
		}
	};

	return (
		<section className="flex flex-col">
			<div ref={filterRowRef}>
				{/* Desktop buttons */}
				<div className="hidden space-x-1 lg:flex">
					{mappedFilters.map((filter, index) => (
						<FilterButton
							key={filter?.btnTitle}
							ref={el => {
								filterButtonRefs.current[index] = el;
							}}
							loading={activeFilter?.btnTitle === filter.btnTitle && delayedPending}
							filter={filter}
							isActive={activeFilter?.btnTitle === filter?.btnTitle}
							size="large"
							onClick={e => {
								e?.preventDefault();
								handleFilterChange(filter);
							}}
						/>
					))}
				</div>

				{/* Mobile buttons */}
				<div className="relative -mx-4 lg:hidden">
					<ScrollArea className="scrollbar-hide flex overflow-x-auto whitespace-nowrap">
						<div className="mx-4 flex space-x-1">
							{mappedFilters.map((filter, index) => (
								<FilterButton
									key={filter?.btnTitle}
									ref={el => {
										filterButtonRefs.current[index] = el;
									}}
									loading={
										activeFilter?.btnTitle === filter.btnTitle && delayedPending
									}
									filter={filter}
									isActive={activeFilter?.btnTitle === filter?.btnTitle}
									size="small"
									onClick={e => {
										e?.preventDefault();
										handleFilterChange(filter);
									}}
									onTouchStart={e => e.stopPropagation()}
								/>
							))}
						</div>
					</ScrollArea>
					<div className="pointer-events-none absolute top-0 left-0 h-[50px] w-4 bg-linear-to-l from-transparent to-white"></div>
					<div className="pointer-events-none absolute top-0 right-0 h-[50px] w-4 bg-linear-to-r from-transparent to-white"></div>
				</div>
			</div>

			<div>
				{currentFilter && currentFilter.showDescription ? (
					<>
						<Spacer size="24" />

						<div className="flex flex-col space-y-2">
							<h4 className="text-muted-navy text-2xl leading-10 font-bold">
								{currentFilter.descriptionTitle}
							</h4>

							<RichTextLexical content={currentFilter.description} />
						</div>
						<Spacer size="96" />
					</>
				) : (
					<Spacer size="96" />
				)}
			</div>

			<ul className="flex flex-wrap justify-center gap-x-4 gap-y-6 p-0 lg:justify-start">
				{currentFilter &&
					(currentFilter.itemsInProductFilter as Course[])?.map((item, i) => {
						return (
							<CourseListCard
								index={i}
								key={item.id}
								course={item as ListCourseType}
							/>
						);
					})}
			</ul>

			{allProductsFilter?.showAllProductsFilter &&
				activeFilter?.btnTitle !== mappedFilters[0]?.btnTitle && (
					<div className="mt-12 flex justify-center">
						<Button
							variant="outlined"
							onClick={e => {
								e?.preventDefault();
								handleFilterChange(mappedFilters[0]);
							}}
						>
							{mappedFilters[0].btnTitle}
						</Button>
					</div>
				)}
		</section>
	);
};
